// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { classList, convertToLocaleId, useLocalMessage } from '@Helpers/';
import { ObjectTypeV1 as ObjectType, AlertOutput, QuerySelector, PermissionsEnum } from 'thehive-sdk';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import useConfigList from '@Hooks/useConfigList';
import client from '@Client';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { Badge, Space } from 'antd';
import { SecurityTag } from '@Atoms/Info';
import { FormatDate, FormatMultipleDate } from '@Molecules/Format';
import { ICONS } from '@Constants/iconsApp';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { useGenericCountQuery } from '@Queries/genericQueries';
import { THLayout } from '@Molecules/THLayout';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import useDisplayParams from '@Molecules/DisplayParams/DisplayParams';
import LinkButton from '@Atoms/Buttons/LinkButton/LinkButton';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import { AlertListTitle } from '@Organisms/Alert/AlertListTitle/AlertListTitle';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { openMessage, handleNotificationError } from '@Helpers/notification';
import { computeEventList } from '@Organisms/Alert/eventList';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { AlertDrawer } from '@Organisms/Alert/AlertDrawer/AlertDrawer';
import { useDrawer } from '@Hooks/useDrawer';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { ScopeContext } from '@Context/ScopeProvider';
import { HTMLColors } from '@Constants/colors';
import IconCount from '@Atoms/IconCount/IconCount';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { useCurrentUser } from '@Queries/userQueries';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';

import './RelatedAlertsList.scss';

const computeSelector = (caseId: string): QuerySelector => ({
    head: [{ name: 'getCase', idOrName: caseId }, { name: 'alerts' }],
    name: `get-case-alerts-${caseId}`,
});

const RelatedAlertList = (): ReactElement => {
    const i = useLocalMessage();

    const { caseId } = useParams<{ caseId: string }>();
    const { data: currentUser } = useCurrentUser();
    const { visible, setVisible, data, setData } = useDrawer<AlertOutput>({});
    const { permissions = [] } = currentUser || {};
    const hasPermissions = useSplitPermissions(permissions, [PermissionsEnum.MANAGE_ALERT_UPDATE]);

    const {
        customStatus: {
            alert: { getStatus },
        },
    } = useContext(ScopeContext);

    const config = useConfigList<AlertOutput>({
        selector: computeSelector(caseId),
        controller: client.CaseController,
        storageId: StorageFilterEnum.ALERT_RELATED,
        objectType: ObjectType.ALERT,
        pagination: 'manual',
    });

    const {
        dataSource = [],
        columnsRenderer,
        stateNeedRefresh: { needRefresh, setNeedRefresh },
        listRefetch,
        countRefetch,
        status = 'loading',
        paginationManager,
        filterManager,
        storagePageManager,
        storageViewManager,
    } = config;

    useDrawer.watch(dataSource, () => {
        if (data) {
            setData(dataSource.find(({ _id }) => _id === data._id));
        }
    });

    const { setDefaultDisplay } = storageViewManager || {};

    const {
        page: storagePage,
        setDisplay,
        setAutoRefresh,
        setOnLoadView,
        setFilterIsOpen,
    } = storagePageManager || { page: null };

    const [showDates, DisplayDates, loadVisibleStateDates] = useDisplayParams({
        occurred: { label: i('common.occurred-date'), visible: storagePage?.display?.occurred ?? true },
        createdAt: { label: i('common.created-date'), visible: storagePage?.display?.createdAt ?? true },
        updatedAt: { label: i('common.updated-date'), visible: storagePage?.display?.updatedAt ?? true },
    });
    const [localAutoRefresh, setLocalAutoRefresh] = useState(storagePage?.autoRefresh || false);
    const [localFilterIsOpen, setLocalFilterIsOpen] = useState(storagePage?.filterIsOpen || false);

    setOnLoadView?.((viewContent) => {
        setLocalAutoRefresh(viewContent?.autoRefresh || false);
        loadVisibleStateDates({
            occurred: viewContent?.display?.occurred ?? true,
            createdAt: viewContent?.display?.createdAt ?? true,
            updatedAt: viewContent?.display?.updatedAt ?? true,
        });
    });

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.AlertController,
                history,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (count, message) => {
                    if (count) {
                        countRefetch();
                    }
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
            }),
        [countRefetch, i, listRefetch],
    );

    const columns = columnsRenderer([
        {
            title: [{ label: i('alert.table.columns.severity'), sortField: 'severity' }],
            className: 'related-alerts-list__column-severity',
            render: ({ severity }, { severity: filterSeverity }): ReactElement => (
                <SecurityTag
                    type="severity"
                    value={severity}
                    hasLabel={false}
                    onClick={(): void => filterSeverity(severity)}
                />
            ),
        },
        {
            title: [{ label: i('alert.table.columns.status'), sortField: 'status' }],
            className: 'related-alerts-list__column-status',
            render: ({ status = '' }, { custom: filterCustom }): ReactElement => {
                const statusConf = getStatus(status);

                return (
                    <ColorLabel
                        color={statusConf?.colour || HTMLColors.DARK_GRAY}
                        label={i({
                            id: `alert.data.status.${convertToLocaleId(statusConf?.value || '')}`,
                            defaultMessage: statusConf?.value,
                        })}
                        className={classList('status-tag')}
                        onClick={
                            status
                                ? () =>
                                      filterCustom({
                                          field: 'status',
                                          values: [status],
                                          operator: 'any',
                                      })
                                : undefined
                        }
                    />
                );
            },
        },
        {
            title: [{ label: i('alert.table.columns.title'), sortField: 'title' }],
            className: 'related-alerts-list__column-title column_preview',
            render: (data, { tags: filterTags, customField: filterCustomField }): ReactElement => (
                <AlertListTitle
                    data={data}
                    onTagsClick={(tagValue, event): void => {
                        const isAltPressed = event.altKey;
                        filterTags([tagValue], isAltPressed);
                    }}
                    onCustomFieldsClick={(customField): void => filterCustomField(customField)}
                    customFieldsIsOpen={storagePage?.display?.customFields}
                    onPreviewClick={() => {
                        setData(data);
                        setVisible(true);
                    }}
                />
            ),
        },
        {
            title: [{ label: i('alert.table.columns.type'), sortField: 'type' }],
            className: 'related-alerts-list__column-type',
            render: ({ type }, { custom }): ReactElement => (
                <LinkButton onClick={(): void => custom({ field: 'type', operator: 'any', values: [type] })}>
                    {type}
                </LinkButton>
            ),
        },
        {
            title: [{ label: i('alert.table.columns.source'), sortField: 'source' }],
            className: 'related-alerts-list__column-source',
            render: ({ source }, { custom }): ReactElement => (
                <LinkButton onClick={(): void => custom({ field: 'source', operator: 'any', values: [source] })}>
                    {source}
                </LinkButton>
            ),
        },
        {
            title: [{ label: i('common.reference'), sortField: 'sourceRef' }],
            className: 'related-alerts-list__column-reference',
            render: ({ sourceRef, externalLink }): ReactElement => (
                <Space>
                    {sourceRef}{' '}
                    {externalLink && (
                        <ExternalLink url={externalLink}>
                            <ICONS.LinkOutlined />
                        </ExternalLink>
                    )}
                </Space>
            ),
        },
        {
            title: [{ label: i('alert.table.columns.observables'), sortField: 'observableCount' }],
            className: 'related-alerts-list__column-observables',
            render: ({ observableCount }): ReactElement => <>{observableCount}</>,
        },
        {
            title: [
                { label: i('common.dates') },
                { label: 'O.', sortField: 'date', hide: !showDates.occurred },
                { label: 'C.', sortField: '_createdAt', hide: !showDates.createdAt },
                { label: 'U.', sortField: '_updatedAt', hide: !showDates.updatedAt },
            ],
            className: 'related-alerts-list__column-date',
            optionalRender: DisplayDates,
            render: ({ extraData, date = 0, _createdAt = 0, _updatedAt = 0 }, { date: filterDate }): ReactElement => (
                <>
                    <FormatMultipleDate
                        dates={[
                            {
                                label: 'O.',
                                value: date,
                                onClick: () => filterDate(date, 'date'),
                                hide: !showDates.occurred,
                            },
                            {
                                label: 'C.',
                                value: _createdAt,
                                onClick: () => filterDate(_createdAt, '_createdAt'),
                                hide: !showDates.createdAt,
                            },
                            {
                                label: 'U.',
                                value: _updatedAt,
                                onClick: () => filterDate(_updatedAt, '_updatedAt'),
                                hide: !showDates.updatedAt,
                            },
                        ]}
                    />

                    {extraData?.importDate && (
                        <THTooltip title={i('alert.table.columns.date.imported')} placement="bottom">
                            <div className="related-alerts-list__column-date__from">
                                <ICONS.ClockCircleOutlined />
                                <FormatDate date={extraData?.importDate} onlyFrom />
                            </div>
                        </THTooltip>
                    )}
                </>
            ),
        },
        {
            className: 'related-alerts-list__column-actions',
            render: ({ _id: alertId }): ReactElement => (
                <ActionsList
                    actionList={{
                        unlink: {
                            label: i('alert.actions.unlink.label'),
                            icon: <ICONS.LinkOutlined />,
                            confirm: true,
                            message: i('alert.actions.unlink.confirm'),
                            query: () => eventList.onUnlinkFromCase(alertId, caseId),
                        },
                    }}
                    isIcon
                    scope="single"
                />
            ),
        },
    ]);

    const { data: describe } = useListAllFields();

    useEffect(() => {
        setDisplay?.('occurred', showDates.occurred);
        setDisplay?.('createdAt', showDates.createdAt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDates]);

    useEffect(() => {
        const currentView = storagePage?.currentView || '';

        setDefaultDisplay?.(currentView, {
            occurred: showDates.occurred,
            createdAt: showDates.createdAt,
        });
    }, [storagePage, showDates, setDefaultDisplay]);

    useEffect(() => {
        setAutoRefresh?.(localAutoRefresh);
    }, [localAutoRefresh]); // eslint-disable-line

    const setLocalFilters = (localFilters: boolean): void => {
        setLocalFilterIsOpen(localFilters);
        setFilterIsOpen?.(localFilters);
        filterManager.setEditFilters(localFilters);
    };

    useEffect(() => {
        setLocalFilterIsOpen(filterManager.editFilters);
    }, [filterManager.editFilters]); // eslint-disable-line

    return (
        <>
            <THLayout>
                <THList>
                    <THList.Header>
                        <THList.Slot name="MANAGE">
                            {storagePageManager && storageViewManager ? (
                                <ManageViewList
                                    pageManager={storagePageManager}
                                    viewManager={storageViewManager}
                                    beforeCreate={(content: any): any => {
                                        filterManager.applyFilters();
                                        return { ...content, filters: filterManager.tempFilters };
                                    }}
                                />
                            ) : null}
                        </THList.Slot>
                        <THList.Slot name="NAVIGATION">
                            {!storagePage?.autoRefresh && (
                                <Badge dot={needRefresh}>
                                    <THTooltip
                                        title={needRefresh ? i('common.refresh') : i('common.update-list')}
                                        placement="bottom"
                                    >
                                        <THButton
                                            size="small"
                                            type={needRefresh ? 'primary' : 'default'}
                                            shape="circle"
                                            onClick={() => {
                                                listRefetch();
                                                countRefetch();
                                                setNeedRefresh(false);
                                            }}
                                        >
                                            <ICONS.SyncOutlined />
                                        </THButton>
                                    </THTooltip>
                                </Badge>
                            )}
                            <ManageSwitch
                                label={i('common.auto-refresh')}
                                value={localAutoRefresh}
                                onChange={(autoRefresh): void => setLocalAutoRefresh(autoRefresh)}
                                icon={<ICONS.SyncOutlined />}
                            />

                            <ManageSwitch
                                label={i('common.filters')}
                                value={localFilterIsOpen}
                                onChange={(editFilters): void => setLocalFilters(editFilters)}
                                icon={<ICONS.FilterOutlined />}
                            />
                        </THList.Slot>
                        <THList.Slot name="CONTENT">
                            {(localFilterIsOpen || !!filterManager.tempFilters.length) && (
                                <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.ALERT]}>
                                    <Footer filterConfig={filterManager} />
                                </THFilter>
                            )}
                        </THList.Slot>
                    </THList.Header>

                    <THList.Content
                        dataSource={[...dataSource].splice(
                            (paginationManager?.page || 0) * (paginationManager?.itemsPerPage || 0),
                            paginationManager?.itemsPerPage || 0,
                        )}
                        columns={columns}
                        rowKey="_id"
                        emptyPlaceholder={
                            <DisplayEmptyData spin={status === 'loading'} label={i('case.related-alerts.none')} />
                        }
                    />

                    <THList.Footer>
                        <THList.Slot name="NAVIGATION">
                            <PaginationBrowse count={dataSource.length} paginationConfig={paginationManager} />
                            <PaginationSize paginationConfig={paginationManager} />
                        </THList.Slot>
                    </THList.Footer>
                </THList>
            </THLayout>
            {data && visible && (
                <AlertDrawer
                    data={data}
                    visible={visible}
                    afterVisible={setVisible}
                    eventList={eventList}
                    hasPermissions={hasPermissions}
                    actionList={{
                        unlink: {
                            label: i('alert.actions.unlink.label'),
                            icon: <ICONS.LinkOutlined />,
                            confirm: true,
                            message: i('alert.actions.unlink.confirm'),
                            query: () => eventList.onUnlinkFromCase(data._id, caseId),
                        },
                    }}
                />
            )}
        </>
    );
};

const MenuIcon = (): ReactElement => {
    const { caseId } = useParams<{ caseId: string }>(); //! it's not beautiful (sorry, it's just for this step)
    const { data: count = 0, refetch: countRefetch } = useGenericCountQuery({
        controller: client.CaseController,
        queryParams: {
            selector: computeSelector(caseId),
        },
        fetchOnStart: true,
    });

    useStreamObserver({ rootId: caseId, objectType: STREAM_EVENTS.CASE }, (): void => {
        countRefetch();
    });

    return count ? (
        <IconCount count={count}>
            <ICONS.ApiOutlined />
        </IconCount>
    ) : (
        <ICONS.ApiOutlined />
    );
};

export const Route: RouteType = {
    menu: {
        label: 'case.layout.nav-bar.related-alerts',
        icon: <ICONS.ApiOutlined />,
        shortcut: {
            enable: true,
            render: <MenuIcon />,
        },
    },
    path: RouteBuilder.compute('cases.related-alerts'),
    content: <RelatedAlertList />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.related-alerts' },
};
